/**
 * Minimális XLSX olvasó böngészőre — nincs npm függőség.
 *
 * Az .xlsx egy ZIP, benne OOXML. A ZIP központi könyvtárát kézzel olvassuk, a
 * deflate-et a `DecompressionStream` bontja ki, az XML-t pedig reguláris
 * kifejezéssel szedjük szét. Ez az Excel által írt, jól formált OOXML-re elég.
 *
 * A `scripts/rozpocet/xlsx.mjs` Node-os változatának párja: ugyanaz a logika,
 * de `Uint8Array` bemenettel és async kitömörítéssel, mert a böngészőben nincs
 * szinkron inflate.
 */

const SIG_EOCD = 0x06054b50;
const SIG_CENTRAL = 0x02014b50;
const SIG_LOCAL = 0x04034b50;

export type SheetRows = Map<number, Map<string, string>>;
export type WorkbookSheet = { name: string; rows: SheetRows };
export type Workbook = { sheets: WorkbookSheet[] };

/** A `DecompressionStream` csak `ArrayBuffer`-alapú nézetet fogad el. */
type Bytes = Uint8Array<ArrayBuffer>;

type ZipEntry = { localOffset: number; method: number; compSize: number };

function readU16LE(buf: Uint8Array, off: number): number {
  return buf[off]! | (buf[off + 1]! << 8);
}

function readU32LE(buf: Uint8Array, off: number): number {
  return (buf[off]! | (buf[off + 1]! << 8) | (buf[off + 2]! << 16) | (buf[off + 3]! << 24)) >>> 0;
}

const utf8 = new TextDecoder("utf-8");

/** Központi könyvtár beolvasása: fájlnév → { offset, method, compSize }. */
function readZipIndex(buf: Uint8Array): Map<string, ZipEntry> {
  // Az EOCD a fájl végén van, de utána még állhat legfeljebb 64 KB komment.
  const minStart = Math.max(0, buf.length - 22 - 0xffff);
  let eocd = -1;
  for (let i = buf.length - 22; i >= minStart; i--) {
    if (readU32LE(buf, i) === SIG_EOCD) {
      eocd = i;
      break;
    }
  }
  if (eocd < 0) throw new Error("Nem érvényes XLSX fájl.");

  const count = readU16LE(buf, eocd + 10);
  let off = readU32LE(buf, eocd + 16);
  const index = new Map<string, ZipEntry>();

  for (let i = 0; i < count; i++) {
    if (readU32LE(buf, off) !== SIG_CENTRAL) {
      throw new Error(`Sérült XLSX: a ZIP központi könyvtár hibás a ${off}. bájtnál.`);
    }
    const method = readU16LE(buf, off + 10);
    const compSize = readU32LE(buf, off + 20);
    const nameLen = readU16LE(buf, off + 28);
    const extraLen = readU16LE(buf, off + 30);
    const commentLen = readU16LE(buf, off + 32);
    const localOffset = readU32LE(buf, off + 42);
    const name = utf8.decode(buf.subarray(off + 46, off + 46 + nameLen));
    index.set(name, { localOffset, method, compSize });
    off += 46 + nameLen + extraLen + commentLen;
  }
  return index;
}

async function inflateRaw(raw: Bytes): Promise<Uint8Array> {
  const source = new ReadableStream<BufferSource>({
    start(controller) {
      controller.enqueue(raw);
      controller.close();
    }
  });
  const stream = source.pipeThrough(new DecompressionStream("deflate-raw"));
  const chunks: Uint8Array[] = [];
  let total = 0;
  const reader = stream.getReader();
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    total += value.length;
  }
  const out = new Uint8Array(total);
  let at = 0;
  for (const chunk of chunks) {
    out.set(chunk, at);
    at += chunk.length;
  }
  return out;
}

async function readZipEntry(
  buf: Bytes,
  index: Map<string, ZipEntry>,
  name: string
): Promise<string> {
  const entry = index.get(name);
  if (!entry) throw new Error(`A ZIP nem tartalmazza: ${name}`);
  if (readU32LE(buf, entry.localOffset) !== SIG_LOCAL) {
    throw new Error(`Sérült XLSX: hibás ZIP helyi fejléc (${name}).`);
  }
  const nameLen = readU16LE(buf, entry.localOffset + 26);
  const extraLen = readU16LE(buf, entry.localOffset + 28);
  const start = entry.localOffset + 30 + nameLen + extraLen;
  const raw = buf.subarray(start, start + entry.compSize);
  // 0 = tárolt, 8 = deflate. Az Excel csak ezt a kettőt használja.
  if (entry.method === 0) return utf8.decode(raw);
  if (entry.method === 8) return utf8.decode(await inflateRaw(raw));
  throw new Error(`Nem támogatott ZIP tömörítés (${entry.method}): ${name}`);
}

const ENTITIES: Record<string, string> = {
  lt: "<",
  gt: ">",
  amp: "&",
  quot: '"',
  apos: "'"
};

function decodeXml(s: string): string {
  return (
    s
      .replace(/&#x([0-9a-fA-F]+);/g, (_, hex: string) => String.fromCodePoint(parseInt(hex, 16)))
      .replace(/&#(\d+);/g, (_, dec: string) => String.fromCodePoint(parseInt(dec, 10)))
      .replace(/&(lt|gt|amp|quot|apos);/g, (_, name: string) => ENTITIES[name] ?? "")
      // Az Excel a soron belüli sortörést így kódolja a megosztott sztringekben.
      .replace(/_x000D_/g, "\n")
  );
}

/** `<t>` elemek szövegének összefűzése — a formázott sztringek több `<t>`-re esnek szét. */
function textOf(fragment: string): string {
  let out = "";
  const re = /<t\b[^>]*?(?:\/>|>([\s\S]*?)<\/t>)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(fragment)) !== null) out += decodeXml(m[1] ?? "");
  return out;
}

function parseSharedStrings(xml: string): string[] {
  const out: string[] = [];
  const re = /<si\b[^>]*?(?:\/>|>([\s\S]*?)<\/si>)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(xml)) !== null) out.push(textOf(m[1] ?? ""));
  return out;
}

/** "A" → 1, "Z" → 26, "AA" → 27. Az oszlopok sorrendjéhez kell, mert "AK" < "D" betű szerint. */
export function columnIndex(letters: string): number {
  let n = 0;
  for (const ch of letters) n = n * 26 + (ch.charCodeAt(0) - 64);
  return n;
}

/** Egy munkalap cellái: `Map<sorszám, Map<oszlopbetű, string>>`. Üres cellák kimaradnak. */
function parseSheet(xml: string, sharedStrings: string[]): SheetRows {
  const rows: SheetRows = new Map();
  const cellRe = /<c\b([^>]*?)(?:\/>|>([\s\S]*?)<\/c>)/g;
  let m: RegExpExecArray | null;
  while ((m = cellRe.exec(xml)) !== null) {
    const attrs = m[1] ?? "";
    const inner = m[2] ?? "";
    const ref = /\br="([A-Z]+)(\d+)"/.exec(attrs);
    if (!ref) continue;
    const type = /\bt="([^"]+)"/.exec(attrs)?.[1] ?? "n";

    let value: string | undefined;
    if (type === "inlineStr") {
      value = textOf(inner);
    } else {
      const v = /<v\b[^>]*?(?:\/>|>([\s\S]*?)<\/v>)/.exec(inner)?.[1];
      if (v === undefined) continue;
      if (type === "s") value = sharedStrings[Number(v)] ?? "";
      else if (type === "e")
        continue; // hibaérték (#REF! stb.)
      else value = decodeXml(v);
    }
    if (value === undefined || value === "") continue;

    const rowNum = Number(ref[2]);
    let row = rows.get(rowNum);
    if (!row) {
      row = new Map();
      rows.set(rowNum, row);
    }
    row.set(ref[1]!, value);
  }
  return rows;
}

/** Beolvas egy .xlsx tartalmat memóriából. Sérült / nem ZIP bemenetnél dob. */
export async function readWorkbookFromBuffer(buf: Bytes): Promise<Workbook> {
  const index = readZipIndex(buf);

  const sharedStrings = index.has("xl/sharedStrings.xml")
    ? parseSharedStrings(await readZipEntry(buf, index, "xl/sharedStrings.xml"))
    : [];

  // A munkalapnevek a workbook.xml-ben vannak, a fájlnevek a rels-ben; a kettőt
  // az r:id köti össze.
  const workbook = await readZipEntry(buf, index, "xl/workbook.xml");
  const rels = await readZipEntry(buf, index, "xl/_rels/workbook.xml.rels");

  const targetById = new Map<string, string>();
  const relRe = /<Relationship\b([^>]*)\/>/g;
  let r: RegExpExecArray | null;
  while ((r = relRe.exec(rels)) !== null) {
    const id = /\bId="([^"]+)"/.exec(r[1] ?? "")?.[1];
    const target = /\bTarget="([^"]+)"/.exec(r[1] ?? "")?.[1];
    if (id && target) targetById.set(id, target.replace(/^\/?xl\//, "").replace(/^\.\//, ""));
  }

  const sheets: WorkbookSheet[] = [];
  const sheetRe = /<sheet\b([^>]*)\/>/g;
  let s: RegExpExecArray | null;
  while ((s = sheetRe.exec(workbook)) !== null) {
    const attrs = s[1] ?? "";
    const name = decodeXml(/\bname="([^"]*)"/.exec(attrs)?.[1] ?? "");
    const rid = /\br:id="([^"]+)"/.exec(attrs)?.[1];
    const target = rid ? targetById.get(rid) : undefined;
    if (!target) continue;
    const xml = await readZipEntry(buf, index, `xl/${target}`);
    sheets.push({ name, rows: parseSheet(xml, sharedStrings) });
  }
  return { sheets };
}
